import { useState, useEffect } from 'react';
import type { Application } from '../types';
import { mockTenants } from '../mockData';

interface VisibilityModalProps {
  app: Application | null;
  onClose: () => void;
  onSave: (visibility: 'all' | 'none' | 'partial', tenantIds?: string[]) => void;
}

export default function VisibilityModal({ app, onClose, onSave }: VisibilityModalProps) {
  const [visibility, setVisibility] = useState<'all' | 'none' | 'partial'>('all');
  const [selectedTenants, setSelectedTenants] = useState<string[]>([]);

  useEffect(() => {
    if (app) {
      if (app.userPermission === 'all') {
        setVisibility('all');
      } else if (app.userPermission === 'none') {
        setVisibility('none');
      } else {
        setVisibility('partial');
        setSelectedTenants(app.visibleTenants || []);
      }
    }
  }, [app]);

  if (!app) return null;

  const handleTenantToggle = (tenantId: string) => {
    setSelectedTenants((prev) =>
      prev.includes(tenantId)
        ? prev.filter((id) => id !== tenantId)
        : [...prev, tenantId]
    );
  };

  const handleSave = () => {
    if (visibility === 'partial') {
      onSave(visibility, selectedTenants);
    } else {
      onSave(visibility);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">设置可见性</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <img
                src={app.icon}
                alt={app.name}
                className="w-12 h-12 rounded-lg object-cover border border-gray-200"
              />
              <div>
                <div className="font-semibold text-gray-900">{app.name}</div>
                <div className="text-sm text-gray-500">{app.version}</div>
              </div>
            </div>
          </div>

          <div className="space-y-4 mb-6">
            <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="visibility"
                checked={visibility === 'all'}
                onChange={() => setVisibility('all')}
                className="w-4 h-4 text-teal-600 focus:ring-teal-500"
              />
              <div>
                <div className="font-medium text-gray-900">全部可见</div>
                <div className="text-sm text-gray-500">所有租户都可以看到此应用</div>
              </div>
            </label>

            <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="visibility"
                checked={visibility === 'none'}
                onChange={() => setVisibility('none')}
                className="w-4 h-4 text-teal-600 focus:ring-teal-500"
              />
              <div>
                <div className="font-medium text-gray-900">全部不可见</div>
                <div className="text-sm text-gray-500">所有租户都无法看到此应用</div>
              </div>
            </label>

            <label className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="visibility"
                checked={visibility === 'partial'}
                onChange={() => setVisibility('partial')}
                className="w-4 h-4 text-teal-600 focus:ring-teal-500 mt-0.5"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900 mb-3">部分租户可见</div>
                {visibility === 'partial' && (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {mockTenants.map((tenant) => (
                      <label
                        key={tenant.id}
                        className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedTenants.includes(tenant.id)}
                          onChange={() => handleTenantToggle(tenant.id)}
                          className="w-4 h-4 text-teal-600 focus:ring-teal-500 rounded"
                        />
                        <span className="text-sm text-gray-700">{tenant.name}</span>
                        <span className={`ml-auto px-2 py-0.5 rounded text-xs ${
                          tenant.type === 'enterprise'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {tenant.type === 'enterprise' ? '企业' : '学校'}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </label>
          </div>

          {visibility === 'partial' && selectedTenants.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
              <div className="text-sm text-blue-800">
                已选择 <span className="font-semibold">{selectedTenants.length}</span> 个租户
              </div>
            </div>
          )}
        </div>

        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium whitespace-nowrap"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium whitespace-nowrap"
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
}
